import { DisconnectReason, RemoteParticipant, Room, RoomEvent } from 'livekit-client';
import { LiveKitConfig } from '../config';
import {
  LiveKitConnectionOptions,
  LiveKitConnectionResult,
  LiveKitError,
  LiveKitEvents,
  LiveKitState,
} from '../types';
import { generateLiveKitToken } from '../utils/tokenGenerator';

/**
 * LiveKit Service for video streaming and joining
 *
 * Handles connection to LiveKit servers, manages room state, and publishes/subscribes to tracks.
 * This service wraps the LiveKit client SDK and provides a simplified interface.
 *
 * Key concepts:
 * - Room: Virtual space where participants meet
 * - Participant: A user in the room (local = you, remote = others)
 * - Track: Audio/video stream from a participant
 * - Publication: Metadata about a track (contains the actual track)
 */
export class LiveKitService {
  private room: Room | null = null;
  private state: LiveKitState = 'idle';
  private events: LiveKitEvents;

  constructor(events: LiveKitEvents = {}) {
    this.events = events;
  }

  /**
   * Connect to a LiveKit room
   */
  async connect(options: LiveKitConnectionOptions): Promise<LiveKitConnectionResult> {
    try {
      // Validate configuration
      const validation = LiveKitConfig.validateConfig();
      if (!validation.isValid) {
        throw new Error(`Invalid configuration: ${validation.errors.join(', ')}`);
      }

      this.updateState('connecting');

      // Create room
      this.room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      // Setup event listeners
      this.setupEventListeners();

      // Connect to room
      const config = LiveKitConfig.getConfig();
      const token = await this.generateToken(
        options.roomName,
        options.participantName,
        options.mode
      );

      await this.room.connect(config.url, token, {
        autoSubscribe: true,
      });

      // Set participant name and log the assigned identity
      if (this.room.localParticipant) {
        this.room.localParticipant.setName(options.participantName);
        console.log('👤 Participant connected:', {
          requestedName: options.participantName,
          assignedIdentity: this.room.localParticipant.identity,
          sid: this.room.localParticipant.sid,
          mode: options.mode,
        });
      }

      // Publish local tracks if streaming
      if (options.mode === 'stream') {
        console.log('📺 Mode STREAM: Publishing local tracks...');
        await this.publishLocalTracks();
      } else {
        console.log('👁️ Mode JOIN: Only subscribing to remote tracks (no local publishing)');
      }

      this.updateState('connected');

      return {
        success: true,
        room: this.room,
        participant: this.room.localParticipant,
      };
    } catch (error: any) {
      const livekitError: LiveKitError = {
        type: 'connection',
        message: error.message || 'Failed to connect to LiveKit room',
        details: error,
      };

      this.handleError(livekitError);
      this.updateState('error');

      return {
        success: false,
        error: livekitError.message,
      };
    }
  }

  /**
   * Disconnect from the room
   */
  async disconnect(): Promise<void> {
    if (this.room) {
      await this.room.disconnect();
      this.room = null;
      this.updateState('disconnected');
    }
  }

  /**
   * Get the current room
   */
  getRoom(): Room | null {
    return this.room;
  }

  /**
   * Get the current state
   */
  getState(): LiveKitState {
    return this.state;
  }

  /**
   * Generate LiveKit token
   */
  private async generateToken(
    roomName: string,
    participantName: string,
    mode: string
  ): Promise<string> {
    const config = LiveKitConfig.getConfig();

    // Generate token with appropriate permissions based on mode
    const canPublish = mode === 'stream'; // Only allow publishing for stream mode
    const canSubscribe = true; // Always allow subscribing

    console.log(`🔑 Generating token for ${mode} mode:`, {
      participantName,
      roomName,
      canPublish,
      canSubscribe,
    });
    console.log('🔧 Config used:', {
      url: config.url,
      hasApiKey: !!config.apiKey,
      hasApiSecret: !!config.apiSecret,
    });

    const token = await generateLiveKitToken(
      config.apiKey,
      config.apiSecret,
      roomName,
      participantName,
      {
        canPublish,
        canSubscribe,
        canPublishData: true,
      }
    );

    console.log('🎫 Generated token length:', token.length);
    return token;
  }

  /**
   * Publish local tracks (camera and microphone)
   */
  private async publishLocalTracks(): Promise<void> {
    if (!this.room?.localParticipant) {
      throw new Error('No local participant available');
    }

    try {
      console.log('🎥 LiveKitService: Starting to enable camera and microphone...');
      await this.room.localParticipant.enableCameraAndMicrophone();
      console.log('✅ LiveKitService: Camera and microphone enabled successfully!');

      // Log the tracks that were published
      setTimeout(() => {
        const videoTracks = this.room?.localParticipant?.videoTrackPublications.size || 0;
        const audioTracks = this.room?.localParticipant?.audioTrackPublications.size || 0;
        console.log(
          `📊 LiveKitService: Published ${videoTracks} video tracks and ${audioTracks} audio tracks`
        );
      }, 1000);
    } catch (error: any) {
      console.error('❌ LiveKitService: Failed to enable camera and microphone:', error);
      const livekitError: LiveKitError = {
        type: 'device',
        message: 'Failed to enable camera and microphone',
        details: error,
      };
      this.handleError(livekitError);
      throw error;
    }
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    if (!this.room) return;

    this.room.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
      console.log('👋 Remote participant joined:', participant.identity);
      this.events.onParticipantJoined?.(participant.identity);
    });

    this.room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
      this.events.onParticipantLeft?.(participant.identity);
    });

    this.room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      console.log('❌ Room disconnected with reason:', reason);
      this.updateState('disconnected');
    });

    this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log('🔗 Connection state changed:', state);
      if (state === 'connected') {
        this.updateState('connected');
      } else if (state === 'disconnected') {
        this.updateState('disconnected');
      }
    });

    // Add more error handling
    this.room.on(RoomEvent.MediaDevicesError, (error: Error) => {
      console.error('🎥 Media devices error:', error);
    });

    this.room.on(RoomEvent.SignalConnected, () => {
      console.log('📡 Signal connected');
    });

    // Track events
    this.room.on(RoomEvent.TrackPublished, (publication, participant) => {
      console.log(
        `📺 Track published by ${participant.identity}:`,
        publication.kind,
        publication.source
      );
    });

    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log(`👁️ Track subscribed from ${participant.identity}:`, track.kind, track.source);
    });

    this.room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      console.log(`❌ Track unsubscribed from ${participant.identity}:`, track.kind, track.source);
    });
  }

  /**
   * Update state and trigger callback
   */
  private updateState(newState: LiveKitState): void {
    this.state = newState;
    this.events.onStateChange?.(newState);
  }

  /**
   * Handle errors
   */
  private handleError(error: LiveKitError): void {
    this.events.onError?.(error);
  }

  /**
   * Cleanup
   */
  dispose(): void {
    this.disconnect();
  }
}
